import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { forwardRef, memo, useEffect, useRef, useState, } from "react";
import { mergeRefs } from "react-merge-refs";
import { Box } from "./Box";
import { ClickBase } from "./ClickBase";
import { Grid } from "./Grid";
import { Stack } from "./Stack";
import { svgClasses } from "./Svg";
import { useTheme } from "./ThemeProvider";
import { Typography } from "./Typography";
import { SlotsProvider } from "../providers/SlotsProvider";
import { alpha, mergeSx, resolveCss, } from "../system";
export const textInputBaseClasses = {
    root: "MinervaTextInputBase-root",
    "input-root": "MinervaTextInputBase-inputRoot",
    input: "MinervaTextInputBase-input",
    "start-slot": "MinervaTextInputBase-startSlot",
    "end-slot": "MinervaTextInputBase-endSlot",
    "start-node": "MinervaTextInputBase-startNode",
    "end-node": "MinervaTextInputBase-endNode",
    disabled: "MinervaTextInputBase-disabled",
    focused: "MinervaTextInputBase-focused",
    error: "MinervaTextInputBase-error",
    "input-wrapper": "MinervaTextInputBase-inputWrapper",
    "label-root": "MinervaTextInputBase-labelRoot",
    "label-wrapper": "MinervaTextInputBase-labelWrapper",
    label: "MinervaTextInputBase-label",
    asterisk: "MinervaTextInputBase-asterisk",
    "sub-label": "MinervaTextInputBase-subLabel",
    "helper-text": "MinervaTextInputBase-helperText",
    "error-text": "MinervaTextInputBase-errorText",
};
export const textInputBaseSlots = {
    label: Typography,
    subLabel: Typography,
    asterisk: Typography,
    inputRoot: ClickBase,
    helperText: Typography,
    errorText: Typography,
};
export const TextInputBase = memo(forwardRef(({ as, readonly, optional, required, error, fullWidth, placeholder, focused: _focused, disabled, label, subLabel = optional ? "Optional" : undefined, helperText, errorText, rootRef, startNode, endNode, bottomNode, sx, slots, slotProps, className, onFocus, onBlur, ...rest }, ref) => {
    const Component = as || "input";
    const theme = useTheme();
    const [focused, setFocused] = useState(_focused);
    const inputRef = useRef(null);
    useEffect(() => {
        setFocused(_focused);
    }, [_focused]);
    return (_jsx(SlotsProvider, { baseSlots: textInputBaseSlots, slots: slots, children: ({ inputRoot: InputRoot, label: Label, subLabel: SubLabel, asterisk: Asterisk, errorText: ErrorText, helperText: HelperText, }) => (_jsxs(Stack, { className: clsx([
                textInputBaseClasses.root,
                disabled && textInputBaseClasses.disabled,
                className,
            ]), sx: mergeSx({
                gap: 1,
                width: fullWidth ? "100%" : "fit-content",
                minWidth: "fit-content",
                color: (theme) => alpha(theme.palette.background.onMain, 0.25),
                [`&.${textInputBaseClasses.disabled}`]: {
                    color: (theme) => theme.palette.disabled.main,
                    [`& .${textInputBaseClasses.asterisk}`]: {
                        color: (theme) => theme.palette.disabled.main,
                    },
                    [`& .${textInputBaseClasses["input-root"]}`]: {
                        color: "inherit",
                        backgroundColor: (theme) => theme.palette.disabled.container,
                        borderColor: (theme) => theme.palette.disabled.main,
                    },
                    [`& .${textInputBaseClasses.label}`]: {
                        color: "inherit",
                    },
                    [`& .${textInputBaseClasses.input}`]: {
                        color: "inherit",
                    },
                },
            }, sx), children: [(label || subLabel || errorText || required) && (_jsxs(Grid, { container: true, alignItems: "center", justifyContent: "space-between", gap: 0.5, className: textInputBaseClasses["label-root"], children: [_jsx(Grid, { item: true, children: _jsxs(Grid, { container: true, gap: 1, className: textInputBaseClasses["label-wrapper"], children: [_jsx(Label, { as: "label", variant: "textFieldLabel", color: "background", invertColors: true, ...slotProps?.label, className: clsx([
                                            textInputBaseClasses.label,
                                            slotProps?.label?.className,
                                        ]), children: label }), required && (_jsx(Asterisk, { variant: "textFieldLabel", color: "error", ...slotProps?.asterisk, className: clsx([
                                            textInputBaseClasses.asterisk,
                                            slotProps?.asterisk?.className,
                                        ]), children: "*" })), subLabel && (_jsx(SubLabel, { variant: "textFieldLabel", ...slotProps?.subLabel, className: clsx([
                                            textInputBaseClasses["sub-label"],
                                            slotProps?.subLabel?.className,
                                        ]), children: subLabel }))] }) }), _jsx(Grid, { item: true, children: errorText && (_jsx(ErrorText, { variant: "textFieldHelperText", color: error ? "error" : "inherit", ...slotProps?.errorText, className: clsx([
                                    textInputBaseClasses["error-text"],
                                    slotProps?.errorText?.className,
                                ]), children: errorText })) })] })), _jsxs(InputRoot, { ref: rootRef, as: "div", disabled: disabled || readonly, focused: focused, disableActiveEffect: true, ...slotProps?.inputRoot, onClick: (e) => {
                        inputRef.current?.focus();
                        slotProps?.inputRoot?.onClick?.(e);
                    }, className: clsx([
                        textInputBaseClasses["input-root"],
                        focused && textInputBaseClasses.focused,
                        error && textInputBaseClasses.error,
                        slotProps?.inputRoot?.className,
                    ]), sx: mergeSx({
                        width: "100%",
                        display: "flex",
                        flex: 1,
                        flexWrap: "nowrap",
                        cursor: "default",
                        gap: 2,
                        position: "relative",
                        paddingX: 3,
                        border: 1,
                        borderColor: (theme) => theme.palette.outline,
                        borderRadius: (theme) => theme.radius.default,
                        color: (theme) => alpha(theme.palette.background.onMain, 0.25),
                        [`&.${textInputBaseClasses.focused}`]: {
                            boxShadow: (theme) => `0px 0px 0px 2px ${alpha(theme.palette.outline, 0.5)}`,
                            [`&.${textInputBaseClasses.error}`]: {
                                boxShadow: (theme) => `0px 0px 0px 2px ${alpha(theme.palette.error.main, 0.5)}`,
                            },
                        },
                        [`&.${textInputBaseClasses.error}`]: {
                            borderColor: (theme) => theme.palette.error.main,
                        },
                        [`& .${svgClasses.root}`]: {
                            width: 4.5,
                            height: 4.5,
                        },
                    }, slotProps?.inputRoot?.sx), children: [startNode && (_jsx(Box, { className: clsx([
                                textInputBaseClasses["start-slot"],
                                textInputBaseClasses["start-node"],
                            ]), sx: {
                                alignSelf: "center",
                            }, children: startNode })), _jsx(Stack, { className: textInputBaseClasses["input-wrapper"], sx: {
                                flexGrow: 1,
                                gap: 0.5,
                                cursor: "text",
                            }, children: _jsx(Component, { ref: mergeRefs([inputRef, ref]), disabled: disabled, "aria-disabled": disabled, required: required, "aria-required": required, readOnly: readonly, "aria-readonly": readonly, size: 1, placeholder: placeholder, className: clsx([textInputBaseClasses.input]), onFocus: (e) => {
                                    onFocus?.(e);
                                    !readonly && setFocused(true);
                                }, onBlur: (e) => {
                                    onBlur?.(e);
                                    !readonly && setFocused(false);
                                }, css: resolveCss({
                                    theme,
                                    sx: {
                                        paddingX: 1,
                                        width: fullWidth ? "100%" : "auto",
                                        minWidth: 3,
                                        flexGrow: 1,
                                        backgroundColor: "transparent",
                                        border: "none",
                                        outline: "none",
                                        minHeight: 10.5,
                                        paddingInline: 0,
                                        paddingBlock: 0,
                                        fontSize: (theme) => theme.typography.textFieldBody.fontSize,
                                        fontFamily: (theme) => theme.typography.textFieldBody.fontFamily,
                                        color: (theme) => theme.palette.background.onMain,
                                        "&::placeholder": {
                                            color: (theme) => alpha(theme.palette.background.onMain, 0.25),
                                        },
                                    },
                                }), ...rest }) }), endNode && (_jsx(Box, { className: clsx([
                                textInputBaseClasses["end-slot"],
                                textInputBaseClasses["end-node"],
                            ]), sx: {
                                paddingTop: undefined,
                                alignSelf: "center",
                            }, children: endNode }))] }), bottomNode, helperText && (_jsx(HelperText, { color: disabled ? "inherit" : error ? "error" : "inherit", variant: "textFieldLabel", ...slotProps?.helperText, className: clsx([
                        textInputBaseClasses["helper-text"],
                        slotProps?.helperText?.className,
                    ]), children: helperText }))] })) }));
}));
//# sourceMappingURL=TextInputBase.js.map